import { ApiErrorResponseWithCode, RequestParams, RequestResponse, getCookie, requestJson, setCookie } from '@vfde-sails/utils';
import { call, put, select } from 'redux-saga/effects';
import { getCaptchaToken, listenForCaptchaVerify, resetCaptcha } from '../../../components/captcha';
import { AddressFormId, Cookies, Domains, Endpoints, HttpStatus, Views } from '../../../enum';
import { setActiveView, setLoading } from '../../App/action';
import { AddressForm } from '../interface';
import { JarvisError, JarvisRequest, JarvisResponse, JarvisSession } from '../../../interface';
import { getUrl } from '../../../util/url';
import { isApiError } from '../../../util/typeGuard';
import { getFixnetProducts } from '../../../util/cms';
import { trackAppError, trackCaptchaShown, trackJarvisError } from '../../../tracking/tracking';
import { Address, getZpApiHandshake } from '@vfde-sails/avc-core';
import { selectSelectedAddress, selectForm } from '../selector';
import { formToAddress } from './converter';
import { isAddressError, isAppError } from './update';
import { getNotificationHeadlineText } from '../../../tracking/util';
import { isStagingEnv } from '../../../util/jarvisHelpers';

export function* captchaSaga() {
    yield call(resetCaptcha);

    const token: string = yield call(getCaptchaToken);

    // if token is valid return early.
    if (token) {
        return;
    }

    // otherwise show captcha
    yield put(setActiveView(Views.CAPTCHA));
    yield put(setLoading(false));

    yield call(trackCaptchaShown);

    // wait for user to verify captcha
    yield call(listenForCaptchaVerify);
    yield put(setActiveView(Views.FORM));
    yield put(setLoading());
}

export function* getJarvisSessionInfo () {
    const result: JarvisSession = {
        zpApiState: yield call(getZpApiHandshake),
        vpCaptchaQaAuthorization: yield call(getCookie, Cookies.QA_CAPTCHA),
    };

    return result;
}

export function* trackError(error: unknown) {
    const appError: boolean = yield call(isAppError, error);
    const addressError: boolean = yield call(isAddressError, error);

    if (appError) {
        const appErrorText: string = yield call(getNotificationHeadlineText, AddressFormId.SERVICE_ERROR);

        yield call(trackAppError, appErrorText);
    }

    if (addressError) {
        const addressErrorText: string = yield call(getNotificationHeadlineText, AddressFormId.ADDRESS_ERROR);

        yield call(trackJarvisError, addressErrorText, error as ApiErrorResponseWithCode<JarvisError>);
    }
}

export function* getJarvisData () {
    const form: AddressForm = yield select(selectForm);
    const selectedAddress: Address | undefined = yield select(selectSelectedAddress);
    const address: Partial<Address> = yield selectedAddress ?? call(formToAddress, form);

    const url: string = isStagingEnv() ? yield call(getUrl, Domains.ST, Endpoints.JARVIS) : yield call(getUrl, Domains.VF, Endpoints.JARVIS);
    const body: JarvisRequest = {
        address,
        captchaToken: yield call(getCaptchaToken),
        ids: yield call(getFixnetProducts),
        sessionInfo: yield call(getJarvisSessionInfo),
    };
    const options: RequestParams = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Api-Key': 'KSZZFe5MPWBLc3cLUPs2zW5at1w7xQyg',
        },
        credentials: 'include',
        body: yield call(JSON.stringify, body),
    };

    const response: RequestResponse<JarvisResponse> = yield call(requestJson, url, options);

    return response.body;
}

/**
 * Gather product availability data from Jarvis
 * @throws {Error | ApiErrorResponseWithCode} - application or network error
 * @returns {JarvisResponse} - result of the jarvis availabilitycheck
 */
export function* jarvisSaga () {


    try {
        const result: JarvisResponse = yield call(getJarvisData);

        return result;
    } catch (error) {
        if (!isApiError(error) || error.status !== HttpStatus.UNAUTHORIZED) {
            throw error;
        }

        yield call(captchaSaga);

        const result: JarvisResponse = yield call(getJarvisData);

        return result;
    }
}
